import { useCallback, useEffect, useRef, useState } from 'react'
import { FanslyError, type FanslyApi } from '../api'
import { asObj, num } from '../data'
import { useFanslyApi } from '../deps'
import { toFanslyError } from '../useFanslyQuery'
import { CHAT_PAGE, chatRowsOf, mergeChatRows, SEGMENTS, sortRecent, type ChatRow, type Segment } from './model'

/** The list asks again this often while the page is on screen. */
export const LIST_POLL_MS = 30_000

const RETRY_STATUS = new Set([0, 408, 429, 502, 503, 504])

/** A read that tries again (up to 3 times) on a busy Fansly or a dropped connection. */
export async function withRetry<T>(read: () => Promise<T>, attempts = 3, waitMs = 250): Promise<T> {
  for (let i = 1; ; i += 1) {
    try {
      return await read()
    } catch (e) {
      const status = e instanceof FanslyError ? e.status : 0
      if (i >= attempts || !RETRY_STATUS.has(status)) throw e
      if (waitMs) await new Promise((r) => setTimeout(r, waitMs * i))
    }
  }
}

export function chatsQuery(segment: Segment, search: string, offset: number, limit = CHAT_PAGE) {
  return { ...SEGMENTS[segment], search, limit, offset }
}

async function readPage(api: FanslyApi, segment: Segment, search: string, offset: number, meId: string) {
  const payload = await withRetry(() => api.get('/crm/chats', chatsQuery(segment, search, offset)))
  return chatRowsOf(payload, meId)
}

export interface ChatListState {
  rows: ChatRow[]
  status: 'loading' | 'ready' | 'error'
  error: FanslyError | null
  hasMore: boolean
  loadingMore: boolean
  refreshing: boolean
  /** Unread messages on the account (Fansly's own count), null until known. */
  unreadTotal: number | null
  loadMore: () => void
  reload: () => void
  /** Patch rows in place (opened → read, sent → new preview, hidden → gone). */
  patch: (update: (rows: ChatRow[]) => ChatRow[]) => void
  /** Ask Fansly for the first page now (after a send) and merge it in. */
  poll: () => void
}

/**
 * The account's conversations for one segment and search: offset pages (more
 * as the list scrolls), the first page asked again every 30 s while the page
 * is visible, and the unread total for the rail badge.
 */
export function useChatList({ segment, search, active, meId }: { segment: Segment; search: string; active: string | null; meId: string }): ChatListState {
  const api = useFanslyApi()
  const [rows, setRows] = useState<ChatRow[]>([])
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<FanslyError | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [unreadTotal, setUnreadTotal] = useState<number | null>(null)
  const seq = useRef(0)
  const offset = useRef(0)
  const busy = useRef(false)
  const activeRef = useRef(active)
  activeRef.current = active
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const [nonce, setNonce] = useState(0)

  const order = useCallback((list: ChatRow[]) => (segment === 'recent' ? sortRecent(list) : list), [segment])

  const readUnread = useCallback(() => {
    api
      .get('/crm/messages/unread')
      .then((r) => setUnreadTotal(num(asObj(r).total)))
      .catch(() => undefined)
  }, [api])

  // A new segment or search starts over.
  useEffect(() => {
    const mine = ++seq.current
    offset.current = 0
    busy.current = true
    setStatus('loading')
    setError(null)
    setRows([])
    setHasMore(false)
    readPage(api, segment, search, 0, meId)
      .then((page) => {
        if (seq.current !== mine) return
        offset.current = page.length
        setRows(order(mergeChatRows([], page, { active: activeRef.current })))
        setHasMore(page.length >= CHAT_PAGE)
        setStatus('ready')
      })
      .catch((e: unknown) => {
        if (seq.current !== mine) return
        setError(toFanslyError(e))
        setStatus('error')
      })
      .finally(() => {
        if (seq.current === mine) busy.current = false
      })
    readUnread()
    // meId only names "You:" previews; a late account/me does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, segment, search, nonce, order, readUnread])

  const loadMore = useCallback(() => {
    if (busy.current || !hasMore) return
    const mine = seq.current
    busy.current = true
    setLoadingMore(true)
    readPage(api, segment, search, offset.current, meId)
      .then((page) => {
        if (seq.current !== mine) return
        offset.current += page.length
        const known = new Set(rowsRef.current.map((r) => r.groupId))
        const added = page.filter((r) => !known.has(r.groupId)).length
        setRows((prev) => order(mergeChatRows(prev, page, { active: activeRef.current })))
        // A page that added nothing new is the end, whatever its size.
        setHasMore(page.length >= CHAT_PAGE && added > 0)
      })
      .catch((e: unknown) => {
        if (seq.current === mine) setError(toFanslyError(e))
        setHasMore(false)
      })
      .finally(() => {
        if (seq.current === mine) {
          busy.current = false
          setLoadingMore(false)
        }
      })
  }, [api, segment, search, meId, hasMore, order])

  const poll = useCallback(() => {
    if (busy.current) return
    const mine = seq.current
    setRefreshing(true)
    readPage(api, segment, search, 0, meId)
      .then((page) => {
        if (seq.current !== mine) return
        setRows((prev) => order(mergeChatRows(prev, page, { prepend: true, active: activeRef.current })))
        setError(null)
      })
      .catch(() => undefined)
      .finally(() => seq.current === mine && setRefreshing(false))
    readUnread()
  }, [api, segment, search, meId, order, readUnread])

  // The list (and the unread total) refreshes every 30 s while the page is visible.
  useEffect(() => {
    if (status !== 'ready') return
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      poll()
    }
    const id = window.setInterval(tick, LIST_POLL_MS)
    const onVisible = () => document.visibilityState === 'visible' && poll()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [status, poll])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  const patch = useCallback((update: (list: ChatRow[]) => ChatRow[]) => setRows((prev) => update(prev)), [])

  return { rows, status, error, hasMore, loadingMore, refreshing, unreadTotal, loadMore, reload, patch, poll }
}
